import { useState } from 'react'

import {
  Avatar,
  BlankSlate,
  Box,
  Grid,
  InfiniteScroll,
  SearchField,
  Spinner,
  Text,
  matchText,
} from '@factorialco/deprecated-design-system'
import { FinanceValueObjectsBudgetStatusEnumValues } from 'generated/resources/datatable.graphql'
import { BudgetFragment, GetBudgetsPaginatedDocument } from 'generated/resources/graphql'
import { orderBy, uniq } from 'lodash'

import SingleChoice from 'modules/core/components/Filter/filters/SingleChoice'
import createComputed from 'modules/core/hooks/createComputed'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { Item } from 'modules/finance/components/Budgets/List/Item'

const useSearchFilter = createComputed((search: string, budgets: BudgetFragment[]) => {
  return budgets.filter((budget) => {
    return matchText(search, budget.name)
  })
})

const STATUS_OPTIONS = () => [
  {
    value: FinanceValueObjectsBudgetStatusEnumValues[0],
    label: i18n.t('budgets.status.budget_risk'),
  },
  {
    value: FinanceValueObjectsBudgetStatusEnumValues[1],
    label: i18n.t('budgets.status.over_budget'),
  },
  {
    value: FinanceValueObjectsBudgetStatusEnumValues[2],
    label: i18n.t('budgets.status.within_budget'),
  },
]

const FILTERED_VALUES = {
  status: 'status',
  owners: 'owners',
}

type directionType = 'asc' | 'desc'

const sortBudgets = createComputed(
  (budgetsToSort: BudgetFragment[], attribute: string, direction: directionType) =>
    orderBy(
      budgetsToSort,
      (budget: BudgetFragment) => {
        switch (attribute) {
          case FILTERED_VALUES.status:
            return budget.currentStatus
        }
      },
      [direction]
    )
)

const filterBudgets = createComputed(
  (budgetsToFilter: BudgetFragment[], attribute: string, value: string | number | undefined) => {
    if (value) {
      return budgetsToFilter.filter((budget) => {
        switch (attribute) {
          case FILTERED_VALUES.status:
            return budget.currentStatus === value
          case FILTERED_VALUES.owners:
            return budget.ownerEmployees
              .map((ownerEmployee) => ownerEmployee.id)
              .includes(Number(value))
        }
      })
    } else {
      return budgetsToFilter
    }
  }
)

const LoadingBudgets = () => (
  <Box
    key="loading-budgets"
    flexDirection="row"
    justifyContent="center"
    alignItems="center"
    width="full"
    paddingY="s24"
  >
    <Spinner />
  </Box>
)

interface BudgetListProps {
  archived?: boolean
  canManageBudgets: boolean
}

export const BudgetList = ({ archived, canManageBudgets }: BudgetListProps) => {
  const {
    employee: { id: employeeId },
  } = useCurrentSession()
  const { search, debouncedSearch, onSearch, clearSearch } = useDebouncedSearch()
  const [direction] = useState<directionType>('asc')
  const {
    nodes,
    loading,
    controls: { fetchMore, hasMore },
  } = usePaginatedQuery(GetBudgetsPaginatedDocument, {
    variables: {
      includeInactive: archived ?? false,
      includeArchived: archived ?? false,
      onlyArchived: archived ?? false,
      ownerOrViewerEmployeeIds: canManageBudgets ? [] : [employeeId],
    },
    extractConnection: (dataP) => dataP?.finance.budgetsConnection,
  })

  const budgets = nodes || []

  const [attribute, setAttribute] = useState<string>('')
  const [value, setValue] = useState<string | number | undefined>()

  const sortedBudgets = sortBudgets(budgets, attribute, direction).get()

  const ownerOptions = uniq(
    budgets
      .map((budget) => {
        return budget.ownerEmployees
      })
      .flat()
  ).map((owner) => {
    return {
      label: owner.fullName,
      value: owner.id,
      component: (
        <Box gap="s8" flexDirection="row" alignItems="center">
          <Avatar label={owner.fullName} url={buildUrl(owner.avatar?.url)} size="s32" />
          <Text.Normal>{owner.fullName}</Text.Normal>
        </Box>
      ),
    }
  })

  const searchedBudgets = useSearchFilter(
    debouncedSearch,
    filterBudgets(sortedBudgets, attribute, value).get()
  ).get()

  if (loading) {
    return <LoadingBudgets />
  }

  if (budgets.length === 0) {
    return (
      <BlankSlate
        title={i18n.t('budgets.list.blank_slate.title')}
        description={i18n.t('budgets.list.blank_slate.description')}
        illustration="money-bag"
      />
    )
  }

  return (
    <>
      <Box paddingBottom="s24" justifyContent="spaceBetween" flexDirection="row">
        <Box flexDirection="row" gap="s12">
          <SingleChoice
            label={i18n.t('budgets.list.filters.owner')}
            options={ownerOptions}
            onValueChange={(choiceValue) => {
              setAttribute(FILTERED_VALUES.owners)
              setValue(choiceValue)
            }}
            value={attribute === FILTERED_VALUES.owners ? value : undefined}
          />
          <SingleChoice
            label={i18n.t('budgets.list.filters.status')}
            options={STATUS_OPTIONS()}
            onValueChange={(choiceValue) => {
              setAttribute(FILTERED_VALUES.status)
              setValue(choiceValue)
            }}
            value={attribute === FILTERED_VALUES.status ? value : undefined}
          />
        </Box>
        <Box width="s200" flexDirection="row">
          <Box flexGrow justifyContent="center" alignItems="flexEnd">
            <SearchField
              placeholder="Search"
              onSearch={onSearch}
              search={search}
              clearSearch={clearSearch}
              collapsable
            />
          </Box>
        </Box>
      </Box>

      <Box
        data-scroll-container
        minHeight={'s600'}
        position="relative"
        overflowY="auto"
        overflowX="hidden"
        style={{ maxHeight: 'calc(100vh - 415px)' }}
      >
        <InfiniteScroll
          threshold={48}
          loadMore={fetchMore}
          hasMore={hasMore}
          loader={<LoadingBudgets />}
        >
          <Grid variant="fixed">
            {searchedBudgets.map((budget: BudgetFragment) => {
              return <Item budget={budget} key={budget.id} />
            })}
          </Grid>
        </InfiniteScroll>
      </Box>
    </>
  )
}
